import React, { useState, useEffect } from 'react';
import { api } from '../../utils/requestInterceptor';
import { toast } from 'react-hot-toast';
import { PlusIcon, XMarkIcon, BoltIcon } from '@heroicons/react/24/outline';
import useRaciAssignments from '../../hooks/useRaciAssignments';

// RACI role descriptions for tooltips
const RACI_DESCRIPTIONS = {
  R: 'Responsible: Person who does the work',
  A: 'Accountable: Person with decision authority',
  C: 'Consulted: Person providing input before work',
  I: 'Informed: Person receiving updates on progress'
};

// RACI role colors
const RACI_COLORS = {
  R: 'bg-green-100 text-green-800',
  A: 'bg-blue-100 text-blue-800',
  C: 'bg-purple-100 text-purple-800',
  I: 'bg-yellow-100 text-yellow-800'
};

const RaciAssignments = ({ entityType, entityId }) => {
  const [users, setUsers] = useState([]);
  const [addingRole, setAddingRole] = useState(null); // 'R', 'A', 'C', 'I' or null
  const [selectedUserId, setSelectedUserId] = useState('');
  
  // Use our custom hook for RACI assignments
  // If entity type is project, pass projectId directly, otherwise pass null
  // and we'll use getAssignments for the specific entity
  const { 
    getAssignments, 
    getPhaseAssignmentsWithRollup,
    updateAssignment, 
    refreshAssignments,
    isLoading: loading, 
    error 
  } = useRaciAssignments(entityType === 'project' ? entityId : null);
  
  // Get assignments for this specific entity, using rolled-up assignments for phases
  let assignments = { R: [], A: [], C: [], I: [] };
  
  if (entityType === 'phase') {
    assignments = getPhaseAssignmentsWithRollup(entityId);
  } else {
    assignments = getAssignments(entityType, entityId) || { R: [], A: [], C: [], I: [] };
  }

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/auth/users');
        if (response.data && response.data.users) {
          setUsers(response.data.users);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, []);

  // Add an assignment using our hook
  const handleAddAssignment = async () => {
    if (!addingRole || !selectedUserId) return;
    
    const success = await updateAssignment(
      entityType, 
      entityId, 
      addingRole, 
      selectedUserId, 
      true
    );
    
    if (success) {
      // Reset form
      setAddingRole(null);
      setSelectedUserId('');
      
      // Ensure we have the latest data
      refreshAssignments();
    }
  };

  // Remove an assignment using our hook
  const handleRemoveAssignment = async (assignmentId, userId, roleType) => {
    // For rolled-up assignments from steps, explain that they need to be removed from the step
    if (roleType === 'R' && entityType === 'phase' && assignments.R.find(a => a.id === assignmentId)?.is_rolled_up) {
      const assignment = assignments.R.find(a => a.id === assignmentId);
      toast.error(`This responsibility is assigned at the step level. Please remove it from the step: ${assignment.source_step_id}`);
      return;
    }
    
    // Update the assignment (remove it)
    const success = await updateAssignment(
      entityType, 
      entityId, 
      roleType, 
      userId, 
      false
    );
    
    if (success) {
      // Ensure we have the latest data
      refreshAssignments();
    }
  };

  // Filter out users who are already assigned to the current role
  const getAvailableUsers = () => {
    if (!addingRole) return [];
    
    const assignedUserIds = assignments[addingRole]?.map(a => a.user_id) || [];
    return users.filter(user => !assignedUserIds.includes(user.id));
  };

  if (loading) {
    return (
      <div className="animate-pulse p-4">
        <div className="h-4 bg-gray-200 rounded mb-4 w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded mb-4 w-1/2"></div>
        <div className="h-4 bg-gray-200 rounded mb-4 w-2/3"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-700 bg-red-100 rounded-md">
        <p>{error}</p>
        <button 
          onClick={() => refreshAssignments()}
          className="mt-2 text-red-700 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          RACI Assignments
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Manage who is Responsible, Accountable, Consulted, and Informed
        </p>
        {entityType === 'phase' && (
          <div className="mt-2 text-sm text-gray-500 bg-gray-50 p-2 rounded border border-gray-200">
            <p>
              <BoltIcon className="inline-block h-4 w-4 mr-1 text-orange-500" /> 
              Responsible assignments with a bolt icon are rolled up from steps within this phase.
            </p>
          </div>
        )}
      </div>
      
      <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
          {/* Responsible section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900 flex items-center">
                <span className={`mr-2 px-2 py-1 rounded-full text-xs ${RACI_COLORS.R}`}>R</span>
                Responsible
                <span className="ml-2 text-xs text-gray-500">{RACI_DESCRIPTIONS.R}</span>
              </h4>
              <button
                onClick={() => {
                  setAddingRole('R');
                  setSelectedUserId('');
                }}
                className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            
            {/* List of responsible users */}
            <ul className="space-y-2">
              {assignments.R.length === 0 ? (
                <li className="text-sm text-gray-500 italic">No responsible users assigned</li>
              ) : (
                assignments.R.map(assignment => (
                  <li 
                    key={assignment.id}
                    className={`flex justify-between items-center p-2 bg-white rounded-md shadow-sm ${
                      assignment.is_rolled_up ? 'border-l-4 border-orange-400' : ''
                    }`}
                  >
                    <div>
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">{assignment.user_name}</p>
                        {assignment.is_rolled_up && (
                          <BoltIcon className="ml-1 h-4 w-4 text-orange-500" title="Rolled up from step" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {assignment.user_email}
                        {assignment.is_rolled_up && assignment.source_step_id && (
                          <span className="ml-1 italic">
                            (from step)
                          </span>
                        )}
                      </p>
                    </div>
                    <button 
                      onClick={() => handleRemoveAssignment(assignment.id, assignment.user_id, 'R')}
                      className={`text-gray-400 hover:text-red-500 ${
                        assignment.is_rolled_up ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title={assignment.is_rolled_up ? "Remove from step first" : "Remove assignment"}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
          
          {/* Accountable section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900 flex items-center">
                <span className={`mr-2 px-2 py-1 rounded-full text-xs ${RACI_COLORS.A}`}>A</span>
                Accountable
                <span className="ml-2 text-xs text-gray-500">{RACI_DESCRIPTIONS.A}</span>
              </h4>
              <button
                onClick={() => {
                  setAddingRole('A');
                  setSelectedUserId('');
                }}
                className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            
            {/* List of accountable users */}
            <ul className="space-y-2">
              {assignments.A.length === 0 ? (
                <li className="text-sm text-gray-500 italic">No accountable users assigned</li>
              ) : (
                assignments.A.map(assignment => (
                  <li 
                    key={assignment.id}
                    className="flex justify-between items-center p-2 bg-white rounded-md shadow-sm"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{assignment.user_name}</p>
                      <p className="text-xs text-gray-500">{assignment.user_email}</p>
                    </div>
                    <button 
                      onClick={() => handleRemoveAssignment(assignment.id, assignment.user_id, 'A')}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
          
          {/* Consulted section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900 flex items-center">
                <span className={`mr-2 px-2 py-1 rounded-full text-xs ${RACI_COLORS.C}`}>C</span>
                Consulted
                <span className="ml-2 text-xs text-gray-500">{RACI_DESCRIPTIONS.C}</span>
              </h4>
              <button
                onClick={() => {
                  setAddingRole('C');
                  setSelectedUserId('');
                }}
                className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            
            {/* List of consulted users */}
            <ul className="space-y-2">
              {assignments.C.length === 0 ? (
                <li className="text-sm text-gray-500 italic">No consulted users assigned</li>
              ) : (
                assignments.C.map(assignment => (
                  <li 
                    key={assignment.id}
                    className="flex justify-between items-center p-2 bg-white rounded-md shadow-sm"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{assignment.user_name}</p>
                      <p className="text-xs text-gray-500">{assignment.user_email}</p>
                    </div>
                    <button 
                      onClick={() => handleRemoveAssignment(assignment.id, assignment.user_id, 'C')}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
          
          {/* Informed section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium text-gray-900 flex items-center">
                <span className={`mr-2 px-2 py-1 rounded-full text-xs ${RACI_COLORS.I}`}>I</span>
                Informed
                <span className="ml-2 text-xs text-gray-500">{RACI_DESCRIPTIONS.I}</span>
              </h4>
              <button
                onClick={() => {
                  setAddingRole('I');
                  setSelectedUserId('');
                }}
                className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            
            {/* List of informed users */}
            <ul className="space-y-2">
              {assignments.I.length === 0 ? (
                <li className="text-sm text-gray-500 italic">No informed users assigned</li>
              ) : (
                assignments.I.map(assignment => (
                  <li 
                    key={assignment.id}
                    className="flex justify-between items-center p-2 bg-white rounded-md shadow-sm"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{assignment.user_name}</p>
                      <p className="text-xs text-gray-500">{assignment.user_email}</p>
                    </div>
                    <button 
                      onClick={() => handleRemoveAssignment(assignment.id, assignment.user_id, 'I')}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>
      
      {/* Modal for adding a new assignment */}
      {addingRole && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setAddingRole(null)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            {/* Modal content */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Add {addingRole === 'R' ? 'Responsible' : 
                            addingRole === 'A' ? 'Accountable' : 
                            addingRole === 'C' ? 'Consulted' : 'Informed'} User
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {RACI_DESCRIPTIONS[addingRole]}
                      </p>
                    </div>
                    
                    <div className="mt-4">
                      <label htmlFor="user-select" className="block text-sm font-medium text-gray-700">
                        Select User
                      </label>
                      <select
                        id="user-select"
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                      >
                        <option value="">Select a user</option>
                        {getAvailableUsers().map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleAddAssignment}
                  disabled={!selectedUserId}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm ${!selectedUserId ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Add Assignment
                </button>
                <button
                  type="button"
                  onClick={() => setAddingRole(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RaciAssignments; 